<script setup lang="ts">
import type { CvCreateMode } from '~/types'

/**
 * Homepage hero: the visitor picks how to create the CV — AI chat (default),
 * one block of text pasted or written at once, or the classic editor form.
 *
 * The modes are kept alive, so switching away and back keeps a half-finished
 * interview or a generated CV. `?mode=paste|form` opens another mode directly.
 *
 * Also the whole of /free-cv-builder, which passes its own source labels so
 * the admin can tell its CVs apart from the homepage's.
 */
const props = withDefaults(defineProps<{
  /** Funnel events for the A/B report (see useHeroExperiment). */
  onEvent?: (name: string, data?: Record<string, unknown>) => void
  /** Label on the chat's saved interview and guest CV. */
  chatSource?: string
  /** Label on the paste generator's guest CV. */
  pasteSource?: string
  /** The paste generator's three-step cards. */
  withSteps?: boolean
  /** Where the plan strip's "compare" goes: an anchor on this page or another page. */
  pricingTo?: string
}>(), {
  onEvent: undefined,
  chatSource: 'ai-chat',
  pasteSource: 'homepage-paste',
  withSteps: false,
  pricingTo: '#pricing',
})

const route = useRoute()
const modes: readonly CvCreateMode[] = ['chat', 'paste', 'form']
// The "e-mail me my CV" link (?draft=…&sig=…) reopens a pasted CV's result,
// which only the paste generator can show.
const initial = typeof route.query.draft === 'string'
  ? 'paste'
  : modes.find(id => id === route.query.mode) ?? 'chat'
const mode = ref<CvCreateMode>(initial)

watch(mode, (value) => {
  props.onEvent?.('hero_mode_switch', { mode: value })
})
</script>

<template>
  <!-- A tinted stage behind all three modes, so the white chat, text box and
       form card stand out; it fades back to white before the next section. -->
  <div class="hero-stage relative isolate overflow-hidden">
    <div class="hero-stage__backdrop" aria-hidden="true">
      <span class="hero-stage__blob hero-stage__blob--green" />
      <span class="hero-stage__blob hero-stage__blob--lilac" />
      <span class="hero-stage__dots" />

      <!-- The template's hand-drawn arrow and doodle, shared by all three modes
           so they stay put when switching; hidden on small screens. Same box
           as the sections' `.shell`. The arrow's tip (y≈162 in the SVG) points
           at the chat's answer box on arrival. -->
      <div class="relative mx-auto h-full max-w-[81rem] px-5 sm:px-6">
        <img
          src="/sofax/hero-1-element-1.svg"
          alt=""
          width="291"
          height="200"
          class="absolute -left-16 top-48 hidden opacity-70 xl:inline-block"
        >
        <img
          src="/sofax/hero-1-element-2.svg"
          alt=""
          width="311"
          height="243"
          class="absolute -right-24 top-0 hidden opacity-70 2xl:inline-block"
        >
      </div>
    </div>

    <Transition name="hero-mode" mode="out-in">
      <KeepAlive>
        <LandingCvChatBuilder v-if="mode === 'chat'" :source="chatSource" variant="chat" :on-event="onEvent">
          <template #switcher>
            <LandingCvModeSwitch v-model="mode" />
          </template>
        </LandingCvChatBuilder>

        <LandingFreeCvBuilder v-else-if="mode === 'paste'" class="sofax" :source="pasteSource" :with-steps="withSteps">
          <template #switcher>
            <LandingCvModeSwitch v-model="mode" />
          </template>
        </LandingFreeCvBuilder>

        <LandingCvFormStart v-else>
          <template #switcher>
            <LandingCvModeSwitch v-model="mode" />
          </template>
        </LandingCvFormStart>
      </KeepAlive>
    </Transition>

    <!-- What is free and what is paid, before anyone scrolls to the price list -->
    <LandingPlanStrip :pricing-to="pricingTo" />
  </div>
</template>

<style scoped>
.hero-stage {
  background: linear-gradient(180deg, #ebe8f2 0%, #efedf4 calc(100% - 9rem), #fff 100%);
}

:global(.dark-mode) .hero-stage {
  background: linear-gradient(180deg, #0b0b12 0%, #111018 calc(100% - 9rem), rgb(3 7 18) 100%);
}

/* The modes paint their own white section; on the stage they let it show. */
.hero-stage :deep(section.bg-white) {
  background: transparent;
}

.hero-stage__backdrop {
  @apply pointer-events-none absolute inset-0 -z-10;
}

/* Two soft glows in the brand colours, drifting slowly behind the card. */
.hero-stage__blob {
  @apply absolute rounded-full blur-3xl;

  will-change: transform;
}

.hero-stage__blob--green {
  @apply -left-32 top-24 h-[26rem] w-[26rem] bg-accent-soft/60 md:left-[8%] dark:bg-accent/20;

  animation: hero-drift 18s ease-in-out infinite alternate;
}

.hero-stage__blob--lilac {
  @apply -right-32 top-4 h-[30rem] w-[30rem] bg-lilac/60 md:right-[6%] dark:bg-lilac/15;

  animation: hero-drift 22s ease-in-out -6s infinite alternate-reverse;
}

/* Faint dot grid, strongest around the middle and gone at the edges. */
.hero-stage__dots {
  @apply absolute inset-0 dark:opacity-40;

  background-image: radial-gradient(rgb(14 14 14 / 0.09) 1px, transparent 1px);
  background-size: 22px 22px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 30%, transparent 75%);
}

:global(.dark-mode) .hero-stage__dots {
  background-image: radial-gradient(rgb(255 255 255 / 0.12) 1px, transparent 1px);
}

@keyframes hero-drift {
  from {
    transform: translate3d(0, 0, 0) scale(1);
  }

  to {
    transform: translate3d(4rem, 3rem, 0) scale(1.12);
  }
}

/* Switching modes: the old one fades out, the new one rises in. */
.hero-mode-enter-active {
  transition: opacity 0.3s ease-out, transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-mode-leave-active {
  transition: opacity 0.12s ease-in;
}

.hero-mode-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.hero-mode-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .hero-stage__blob {
    animation: none;
  }

  .hero-mode-enter-active,
  .hero-mode-leave-active {
    transition: none;
  }

  .hero-mode-enter-from {
    transform: none;
  }
}
</style>
